"use client";

import { useMemo, useState } from "react";
import type { IFaqItem } from "@/lib/sanity/types";
import { FaqAccordion } from "./FaqAccordion";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type TFaqAudienceFilter = "all" | "employee" | "employer";

interface IFaqFilterSectionProps {
  items: IFaqItem[];
  className?: string;
  /**
   * Optional title row shown above the filter controls.
   * Use `h2` for embedded page sections.
   */
  heading?: string;
  /**
   * "split" renders the redesign's two-column layout: intro + filter pills on
   * the left (sticky on large screens), accordion on the right.
   */
  variant?: "stacked" | "split";
  /** Extra content under the intro column in the split variant. */
  aside?: React.ReactNode;
  /** Intro paragraph under the heading (split variant). */
  intro?: string;
  /** Heading element for the split variant ("h1" on /faq, "h2" on home). */
  headingLevel?: "h1" | "h2";
}

function filterItems(
  items: IFaqItem[],
  filter: TFaqAudienceFilter
): IFaqItem[] {
  if (filter === "all") return items;
  if (filter === "employee") {
    return items.filter((i) => i.tags.includes("employee"));
  }
  // "employer" audience means employer+owner.
  return items.filter((i) => i.tags.includes("employer") || i.tags.includes("owner"));
}

export function FaqFilterSection({
  items,
  className,
  heading,
  variant = "stacked",
  aside,
  intro,
  headingLevel = "h2",
}: IFaqFilterSectionProps): React.ReactElement {
  const [filter, setFilter] = useState<TFaqAudienceFilter>("all");

  const filtered = useMemo(
    () => filterItems(items ?? [], filter),
    [items, filter]
  );

  const hasItems = items != null && items.length > 0;

  if (variant === "split") {
    const HeadingTag = headingLevel;
    const filters: Array<{ id: TFaqAudienceFilter; label: string }> = [
      { id: "all", label: "All" },
      { id: "employee", label: "Employees" },
      { id: "employer", label: "Employers" },
    ];
    return (
      <div
        className={cn(
          "grid items-start gap-10 lg:grid-cols-[minmax(0,.62fr)_minmax(0,1fr)] lg:gap-16",
          className
        )}
      >
        <div className="lg:sticky lg:top-[104px]">
          <div className="font-sans text-xs font-bold uppercase tracking-[.12em] text-primary">
            FAQ
          </div>
          <HeadingTag className="mt-4 text-balance font-sans text-3xl font-bold leading-[1.04] tracking-tight text-foreground sm:text-4xl">
            {heading ?? "Frequently Asked Questions"}
          </HeadingTag>
          <p className="mt-4 font-sans text-[17px] leading-relaxed text-muted-foreground">
            {intro ??
              "Get quick answers about how Tippo helps restaurants split tips fairly and stay compliant."}
          </p>
          {hasItems && (
            <div className="mt-6 flex flex-wrap gap-2.5">
              {filters.map(({ id, label }) => (
                <Button
                  key={id}
                  type="button"
                  variant={filter === id ? "default" : "outline"}
                  size="sm"
                  className="rounded-full px-4"
                  onClick={() => setFilter(id)}
                  aria-pressed={filter === id}
                >
                  {label}
                </Button>
              ))}
            </div>
          )}
          {aside}
        </div>
        {hasItems ? (
          <FaqAccordion items={filtered} />
        ) : (
          <p className="text-muted-foreground">FAQ coming soon.</p>
        )}
      </div>
    );
  }

  return (
    <div className={cn("space-y-6", className)} aria-label="FAQ">
      {heading && <h2 className="text-2xl font-bold tracking-tight">{heading}</h2>}

      {hasItems && (
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button
            type="button"
            variant={filter === "all" ? "default" : "secondary"}
            size="sm"
            onClick={() => setFilter("all")}
            aria-pressed={filter === "all"}
          >
            All
          </Button>
          <Button
            type="button"
            variant={filter === "employee" ? "default" : "secondary"}
            size="sm"
            onClick={() => setFilter("employee")}
            aria-pressed={filter === "employee"}
          >
            Employees
          </Button>
          <Button
            type="button"
            variant={filter === "employer" ? "default" : "secondary"}
            size="sm"
            onClick={() => setFilter("employer")}
            aria-pressed={filter === "employer"}
          >
            Employers
          </Button>
        </div>
      )}

      {hasItems ? (
        <FaqAccordion items={filtered} />
      ) : (
        <p className="text-muted-foreground">FAQ coming soon.</p>
      )}
    </div>
  );
}

